import React, { useMemo, useState, useRef } from 'react';
import { MdChevronLeft, MdChevronRight, MdClose } from 'react-icons/md';
import { View } from 'react-native';
import { TimelineItemRow, EditorHeader, PlatformIcon, getTravelBackgroundAssetNoFallback } from '@cubemeet/ui';
import CardRenderer from '../../components/CardRenderer';
import ItineraryTabs from '../../components/ItineraryTabs';
import editorStyles from '../../components/ItineraryEditor.styles';
import { SnapshotDay, SnapshotEntry, SnapshotNote, TravelPostPreviewDraft } from '../types';
import { parseTimeToMinutes, compareTimelineItems } from '../../timeUtils';
import { supabase } from '@cubemeet/core/services/supabase';

const CARD_IMAGE_UPLOAD_ENDPOINT = 
  (typeof window !== 'undefined' && (window as any).__CARD_IMAGE_UPLOAD__) || 
  'https://card-image-upload.cubemeet.app';

interface TravelPostPreviewLayoutProps {
  loading: boolean;
  error?: string | null;
  draft: TravelPostPreviewDraft | null;
  days: SnapshotDay[];
  timelineDays: any[];
  hiddenEntryIds: Set<string>;
  selectedDayIndex: number;
  onSelectDay: (index: number) => void;
  onRemoveEntry: (dayId: string, entryId: string) => void;
  onRestoreEntry: (dayId: string, entryId: string) => void;
  onAddNote?: (dayId: string, entryId: string, text: string) => Promise<void> | void;
  onUpdateCardImage?: (cardId: string, gallery: any[]) => Promise<void> | void;
  onRestoreAll: () => void;
  onPublish: () => void;
  publishing: boolean;
  onUpdateTitle?: (title: string) => void;
  onUpdateSummary?: (summary: string) => void;
  onUpdateTags?: (tags: string[]) => void;
  onUpdateSummaryAndTags?: (summary: string, tags: string[]) => void;
}

const getEntryId = (item: any): string => {
  return String(
    item?.id
      || item?.entry_id
      || item?.meta?.itineraryEntryId
      || item?.meta?.itinerary_entry_id
      || item?.card_id
      || item?.placeholder_id
      || `entry-${item?.time || ''}`,
  );
};

const adaptSnapshotEntryToTimelineItem = (entry: SnapshotEntry) => ({
  id: entry.sourceEntryId || entry.id,
  time: entry.meta?.start_time || '09:00',
  title: entry.title,
  note: entry.summary,
  summary: entry.summary,
  card: {
    image_gallery: entry.gallery?.map((img) => ({ url: img.url, alt: img.alt })),
    description: entry.summary,
  },
  children: entry.notes?.map((note) => ({ id: note.id, note: note.text })),
});

const EntryCard: React.FC<{
  item: any;
  snapshotEntry?: SnapshotEntry;
  hidden: boolean;
  onRemove: () => void;
  onRestore: () => void;
  onUploadImage?: () => void;
  uploading?: boolean;
  onImageClick?: (images: any[], startIndex: number) => void;
}> = ({ item, snapshotEntry, hidden, onRemove, onRestore, onUploadImage, uploading, onImageClick }) => {
  const actionLabel = hidden ? '恢復' : '移除';
  const dayDate = snapshotEntry?.meta?.date_str || snapshotEntry?.meta?.dateStr || snapshotEntry?.meta?.date;

  return (
    <div style={{ position: 'relative', opacity: hidden ? 0.4 : 1, transition: 'opacity 0.2s ease', width: '100%', maxWidth: '100%', minWidth: 0 }}>
      <CardRenderer it={item} dayDate={dayDate} onImageClick={onImageClick} />
      
      {/* 按钮组 */}
      <div style={{
        position: 'absolute',
        top: 16,
        right: 16,
        display: 'flex',
        gap: 8,
        alignItems: 'center',
      }}>
        {/* 相机上传按钮 - 只在有 cardId 且非隐藏时显示 */}
        {!hidden && snapshotEntry?.meta?.card_id && onUploadImage && (
          <button
            type="button"
            onClick={onUploadImage}
            disabled={uploading}
            style={{
              border: 'none',
              background: uploading ? 'rgba(200,200,200,0.92)' : 'rgba(255,255,255,0.92)',
              borderRadius: 999,
              padding: '8px',
              width: 36,
              height: 36,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: uploading ? 'not-allowed' : 'pointer',
              boxShadow: '0 6px 16px rgba(0,0,0,0.1)',
              transition: 'all 0.2s ease',
            }}
            title="上傳圖片"
          >
            {uploading ? (
              <PlatformIcon name="hourglass_empty" size={18} color="#9CA3AF" family="MaterialIcons" />
            ) : (
              <PlatformIcon name="photo_camera" size={18} color="#374151" family="MaterialIcons" />
            )}
          </button>
        )}
        
        {/* 移除/恢复按钮 */}
        <button
          type="button"
          onClick={hidden ? onRestore : onRemove}
          style={{
            border: 'none',
            background: 'rgba(255,255,255,0.92)',
            borderRadius: 999,
            padding: '8px',
            width: 36,
            height: 36,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 6px 16px rgba(0,0,0,0.1)',
            transition: 'all 0.2s ease',
          }}
          title={actionLabel}
        >
          {hidden ? (
            <PlatformIcon name="restore" size={18} color="#059669" family="MaterialIcons" />
          ) : (
            <PlatformIcon name="close" size={18} color="#DC2626" family="MaterialIcons" />
          )}
        </button>
      </div>
    </div>
  );
};

const EntryNotesList: React.FC<{ notes?: SnapshotNote[] }> = ({ notes }) => {
  if (!notes || !notes.length) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
      {notes.map((note) => {
        // 去重圖片，使用 URL 作為唯一鍵
        const uniqueImages = note.images && note.images.length
          ? Array.from(new Map(note.images.map(img => [img.url, img])).values()).slice(0, 3)
          : [];
        
        return (
          <div
            key={note.id}
            style={{
              background: '#FDFDFE',
              borderRadius: 18,
              border: '1px solid #E5E7EB',
              padding: '12px 16px',
              boxShadow: '0 4px 14px rgba(15,23,42,0.06)',
            }}
          >
            <div style={{ fontSize: 14, color: '#374151', lineHeight: 1.6 }}>{note.text}</div>
            {uniqueImages.length > 0 ? (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                {uniqueImages.map((img, index) => (
                  <img
                    key={img.key || img.url || index}
                    src={img.url}
                    alt={img.alt || 'note-media'}
                    style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 12 }}
                  />
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

const TravelPostPreviewLayout: React.FC<TravelPostPreviewLayoutProps> = ({
  loading,
  error,
  draft,
  days,
  timelineDays,
  hiddenEntryIds,
  selectedDayIndex,
  onSelectDay,
  onRemoveEntry,
  onRestoreEntry,
  onAddNote,
  onUpdateCardImage,
  onRestoreAll,
  onPublish,
  publishing,
  onUpdateTitle,
  onUpdateSummary,
  onUpdateTags,
  onUpdateSummaryAndTags,
}) => {
  const [activeComposer, setActiveComposer] = useState<string | null>(null);
  const [noteDrafts, setNoteDrafts] = useState<Record<string, string>>({});
  const [savingEntryId, setSavingEntryId] = useState<string | null>(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [isEditingSummary, setIsEditingSummary] = useState(false);
  const [summaryDraft, setSummaryDraft] = useState('');
  const [tagsDraft, setTagsDraft] = useState<string[]>([]);
  const [showTagPicker, setShowTagPicker] = useState(false);
  const [tagSearch, setTagSearch] = useState('');
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [uploadingCardId, setUploadingCardId] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // Fetch available tags
  React.useEffect(() => {
    const fetchTags = async () => {
      try {
        const { data } = await supabase.from('travel_posts').select('tags').not('tags', 'is', null);
        if (data) {
          const allTags = new Set<string>();
          data.forEach(post => {
            if (Array.isArray(post.tags)) {
              post.tags.forEach((t: string) => allTags.add(t));
            }
          });
          setAvailableTags(Array.from(allTags));
        }
      } catch (e) {
        console.warn('Failed to fetch tags:', e);
      }
    };
    fetchTags();
  }, []);

  // Filter tags based on search
  const filteredTags = useMemo(() => {
    if (!tagSearch) return availableTags.slice(0, 10); // Show top 10 if no search
    return availableTags.filter(t => t.toLowerCase().includes(tagSearch.toLowerCase())).slice(0, 10);
  }, [availableTags, tagSearch]);

  const addTag = (tag: string) => {
    if (!tagsDraft.includes(tag)) {
      setTagsDraft([...tagsDraft, tag]);
    }
    setTagSearch('');
    setShowTagPicker(false);
  };

  const removeTag = (tag: string) => {
    setTagsDraft(tagsDraft.filter(t => t !== tag));
  };

  // 获取当前用户 ID
  React.useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const authRes: any = await supabase.auth.getUser();
        const user = authRes?.data?.user || (supabase.auth && (supabase.auth.user && supabase.auth.user()));
        if (user) {
          setCurrentUserId(user.id);
        }
      } catch (e) {
        console.warn('Failed to get current user:', e);
      }
    };
    getCurrentUser();
  }, []);

  // FAB bottom inset so it won't be hidden behind app bottom nav on web
  const [fabBottomInset, setFabBottomInset] = useState<number>(28);
  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return undefined;
    let mounted = true;
    const computeInset = () => {
      try {
        let navEl: Element | null = document.getElementById('app-bottom-nav-portal');
        if (navEl && navEl.children && navEl.children.length === 1) {
          navEl = navEl.children[0] as Element;
        }
        if (!navEl) {
          const fixedEls = Array.from(document.querySelectorAll('*')).filter((e) => getComputedStyle(e as Element).position === 'fixed');
          navEl = fixedEls.find((e) => {
            try {
              const r = (e as Element).getBoundingClientRect();
              return Math.abs(r.bottom - window.innerHeight) < 8 && r.height > 20;
            } catch (e) { return false; }
          }) || null;
        }
        if (!mounted) return;
        if (navEl) {
          const rect = navEl.getBoundingClientRect();
          const inset = Math.ceil(rect.height + 16);
          setFabBottomInset(inset);
          return;
        }
      } catch (e) {
        // ignore
      }
      if (mounted) setFabBottomInset(28);
    };
    window.addEventListener('resize', computeInset);
    const mo = new MutationObserver(computeInset);
    try { mo.observe(document.body, { childList: true, subtree: true }); } catch (e) {}
    computeInset();
    return () => {
      mounted = false;
      window.removeEventListener('resize', computeInset);
      try { mo.disconnect(); } catch (e) {}
    };
  }, []);

  // 处理图片上传
  const handleImageUpload = async (cardId: string, file: File) => {
    if (!currentUserId || !onUpdateCardImage) return;

    console.log('[handleImageUpload] Starting upload:', {
      cardId,
      currentUserId,
      fileName: file.name,
    });

    try {
      setUploadingCardId(cardId);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('userId', currentUserId);
      formData.append('cardId', cardId);
      formData.append('folder', 'gallery');

      const resp = await fetch(CARD_IMAGE_UPLOAD_ENDPOINT, {
        method: 'POST',
        body: formData,
        mode: 'cors',
      });

      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(`Upload failed: ${resp.status} ${text}`);
      }

      const result = await resp.json();
      if (!result.success || !result.data) {
        throw new Error('Invalid response from upload worker');
      }

      // 获取当前卡片的 gallery
      const { data: cardData, error: fetchError } = await supabase
        .from('cards_cards')
        .select('image_gallery')
        .eq('id', cardId)
        .single();

      if (fetchError) throw fetchError;

      const currentGallery = Array.isArray(cardData?.image_gallery) ? cardData.image_gallery : [];

      // 构造新的 gallery item - 添加 itinerary_id 标记
      const itineraryId = draft?.previewItinerary?.id || draft?.id;
      console.log('[TravelPostPreview] Upload image with itinerary_id:', itineraryId, 'draft:', draft);
      
      const newImage = {
        url: result.data.url,
        key: result.data.key,
        uploaded_by: currentUserId,
        uploaded_at: result.data.uploadTime || new Date().toISOString(),
        itinerary_id: itineraryId, // 标记来自哪个行程
      };

      console.log('[handleImageUpload] Created newImage object:', newImage);
      console.log('[handleImageUpload] Current gallery before update:', currentGallery);

      // 追加到现有 gallery
      const updatedGallery = [...currentGallery, newImage];
      
      console.log('[handleImageUpload] Updated gallery to write to DB:', updatedGallery);

      // 更新数据库
      const { error: updateError } = await supabase
        .from('cards_cards')
        .update({ image_gallery: updatedGallery })
        .eq('id', cardId);

      if (updateError) throw updateError;

      // 调用回调更新本地状态
      await onUpdateCardImage(cardId, updatedGallery);

      if (typeof window !== 'undefined' && window.alert) {
        window.alert('圖片上傳成功！');
      }
    } catch (err) {
      console.error('Image upload error:', err);
      if (typeof window !== 'undefined' && window.alert) {
        window.alert(`上傳失敗: ${err instanceof Error ? err.message : '未知錯誤'}`);
      }
    } finally {
      setUploadingCardId(null);
      // 清空文件输入
      if (fileInputRefs.current[cardId]) {
        fileInputRefs.current[cardId]!.value = '';
      }
    }
  };

  const entryLookup = useMemo(() => {
    const map = new Map<string, { dayId: string; entry: SnapshotEntry }>();
    days.forEach((day) => {
      day.entries.forEach((entry) => {
        const primary = entry.sourceEntryId || entry.id;
        if (primary) {
          map.set(String(primary), { dayId: day.id, entry });
        }
        map.set(String(entry.id), { dayId: day.id, entry });
      });
    });
    return map;
  }, [days]);

  const inferredTabDays = timelineDays && timelineDays.length
    ? timelineDays
    : days.map((day) => ({
        ...day,
        day: day.dayNumber || day.day,
        items: day.entries.map(adaptSnapshotEntryToTimelineItem),
      }));

  const displayTabs = useMemo(() => inferredTabDays.map((day) => ({ ...day, date: undefined })), [inferredTabDays]);

  const safeIndex = displayTabs.length ? Math.min(Math.max(selectedDayIndex, 0), displayTabs.length - 1) : 0;
  const currentTimelineDay = displayTabs[safeIndex];
  const currentEntries = Array.isArray(currentTimelineDay?.items) ? currentTimelineDay.items : [];
  const sortedEntries = useMemo(
    () => currentEntries.slice().sort((a: any, b: any) => compareTimelineItems(a, b, parseTimeToMinutes)),
    [currentEntries],
  );

  const composedEntries = sortedEntries.map((item: any, index: number) => {
    const entryId = getEntryId(item);
    const snapshotMeta = entryLookup.get(entryId);
    const hidden = hiddenEntryIds.has(entryId) || snapshotMeta?.entry.hidden;
    const removeHandler = () => {
      if (!snapshotMeta) return;
      onRemoveEntry(snapshotMeta.dayId, snapshotMeta.entry.id);
    };
    const restoreHandler = () => {
      if (!snapshotMeta) return;
      onRestoreEntry(snapshotMeta.dayId, snapshotMeta.entry.id);
    };
    return {
      item,
      entryId,
      snapshotEntry: snapshotMeta?.entry,
      hidden,
      removeHandler,
      restoreHandler,
      showConnector: index < sortedEntries.length - 1,
      dayId: snapshotMeta?.dayId,
    };
  });

  const canComposeNotes = typeof onAddNote === 'function';

  const openComposer = (entryId: string) => {
    if (!canComposeNotes) return;
    setActiveComposer(entryId);
    setNoteDrafts((prev) => ({ ...prev, [entryId]: prev[entryId] || '' }));
  };

  const closeComposer = (entryId: string) => {
    setActiveComposer((prev) => (prev === entryId ? null : prev));
    setNoteDrafts((prev) => ({ ...prev, [entryId]: '' }));
    setSavingEntryId((prev) => (prev === entryId ? null : prev));
  };

  const updateComposerValue = (entryId: string, value: string) => {
    setNoteDrafts((prev) => ({ ...prev, [entryId]: value }));
  };

  const submitComposer = async (entryId: string, dayId?: string) => {
    if (!canComposeNotes || !dayId) return;
    const text = (noteDrafts[entryId] || '').trim();
    if (!text) return;
    try {
      setSavingEntryId(entryId);
      await onAddNote?.(dayId, entryId, text);
      setNoteDrafts((prev) => ({ ...prev, [entryId]: '' }));
      setActiveComposer(null);
    } catch (err) {
      console.error('Failed to add note from preview', err);
      if (typeof window !== 'undefined') {
        window.alert?.('新增筆記失敗，請稍後再試');
      }
    } finally {
      setSavingEntryId((prev) => (prev === entryId ? null : prev));
    }
  };

  // 灯箱状态
  const [lightboxState, setLightboxState] = useState<{
    images: any[];
    currentIndex: number;
  } | null>(null);

  const openLightbox = (images: any[], startIndex: number) => {
    setLightboxState({ images, currentIndex: startIndex });
  };

  const closeLightbox = () => {
    setLightboxState(null);
  };

  const nextImage = () => {
    if (!lightboxState) return;
    setLightboxState({
      ...lightboxState,
      currentIndex: (lightboxState.currentIndex + 1) % lightboxState.images.length
    });
  };

  const prevImage = () => {
    if (!lightboxState) return;
    setLightboxState({
      ...lightboxState,
      currentIndex: (lightboxState.currentIndex - 1 + lightboxState.images.length) % lightboxState.images.length
    });
  };

  if (loading) {
    return <div style={{ padding: 48, textAlign: 'center' }}>載入中...</div>;
  }

  if (error) {
    return <div style={{ padding: 48, textAlign: 'center', color: '#DC2626' }}>{error}</div>;
  }

  if (!draft || !days.length) {
    return <div style={{ padding: 48, textAlign: 'center' }}>目前沒有可以預覽的內容</div>;
  }

  // 解析图片 URL，处理相对路径
  const resolveImageUrl = (url: string | undefined) => {
    if (!url) return undefined;
    
    // 如果是完整的 HTTP(S) URL，直接返回
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    
    // 如果是相对路径，转换为绝对路径
    if (url.startsWith('/')) {
      const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
      return `${baseUrl}${url}`;
    }
    
    return url;
  };

  const resolveCoverUri = () => {
    if (draft.previewItinerary?.coverImage) return draft.previewItinerary.coverImage;
    if (draft.coverImage) return draft.coverImage;
    for (const day of days) {
      for (const entry of day.entries) {
        if (entry.coverImage?.url) return entry.coverImage.url;
        if (entry.gallery && entry.gallery.length && entry.gallery[0]?.url) {
          return entry.gallery[0].url;
        }
      }
    }
    return undefined;
  };

  // 使用与 ItineraryEditor 相同的资源解析逻辑
  // 这样可以正确处理本地资源路径（如 assets/images/churches.webp）
  const coverUri = resolveCoverUri();
  let backgroundSource: any = undefined;
  
  if (coverUri) {
    // 先尝试作为本地资源解析（如 assets/images/xxx.webp）
    const assetSource = getTravelBackgroundAssetNoFallback(coverUri);
    if (assetSource) {
      backgroundSource = assetSource;
    } else {
      // 否则作为 URL 处理
      const resolvedUrl = resolveImageUrl(coverUri);
      backgroundSource = resolvedUrl ? { uri: resolvedUrl } : undefined;
    }
  }
  const resolvedTitle = draft.title || draft.previewItinerary?.title || '未命名指南';
  const resolvedSummary = draft.summary || draft.previewItinerary?.summary || '';
  const resolvedCity = draft.city || draft.previewItinerary?.city || '';
  const statsLine = `${draft.meta.dayCount} 天 · ${draft.meta.entryCount} 卡片${resolvedCity ? ` · ${resolvedCity}` : ''}`;

  const handleBack = () => {
    if (typeof window === 'undefined') return;
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = '/';
    }
  };

  

  return (
    <div style={{ width: '100%' }}>
      {/* 全屏頭部 */}
      <View style={editorStyles.headerContainer}>
        <EditorHeader
          title={resolvedTitle}
          subtitle={resolvedCity}
          backgroundSource={backgroundSource}
          onBack={handleBack}
          onEdit={() => {
            if (!onUpdateTitle) return;
            setTitleDraft(resolvedTitle);
            setIsEditingTitle(true);
          }}
        />
        <View style={editorStyles.tabsWrapper}>
          <ItineraryTabs days={displayTabs} selectedIndex={safeIndex} onSelect={onSelectDay} />
        </View>
      </View>

      {/* 內容區域 - 600寬度居中 */}
      <div style={{ maxWidth: 600, margin: '0 auto', padding: '24px 16px 80px', width: '100%', boxSizing: 'border-box' }}>
      <section style={{ textAlign: 'center', marginBottom: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <div style={{ color: '#6B7280', fontSize: 16 }}>遊記摘要（選填）</div>
          {onUpdateSummary && !isEditingSummary && (
            <button
              type="button"
              onClick={() => {
                setSummaryDraft(resolvedSummary || '');
                setTagsDraft(draft?.tags || []);
                setIsEditingSummary(true);
              }}
              title="添加遊記摘要"
              style={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                border: 'none',
                background: '#F3F4F6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#E5E7EB'; (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.05)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#F3F4F6'; (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)'; }}
            >
              <PlatformIcon name="note_add" size={20} color="#6B7280" family="MaterialIcons" />
            </button>
          )}
        </div>
        
        {/* 中間的標題顯示已移除，編輯請使用上方 Header 的按鈕 */}

        {/* Title edit modal */}
        {isEditingTitle && (
          <div
            role="dialog"
            aria-modal="true"
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.5)',
              zIndex: 100000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 16,
            }}
            onClick={() => setIsEditingTitle(false)}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: '#fff',
                borderRadius: 12,
                padding: 20,
                width: '100%',
                maxWidth: 640,
                boxSizing: 'border-box',
                boxShadow: '0 8px 32px rgba(0,0,0,0.16)',
              }}
            >
              <h2 style={{ margin: '0 0 12px 0' }}>編輯標題</h2>
              <input
                type="text"
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                placeholder="輸入標題"
                autoFocus
                style={{
                  fontSize: 20,
                  padding: '10px 12px',
                  width: '100%',
                  boxSizing: 'border-box',
                  borderRadius: 8,
                  border: '1px solid #E5E7EB',
                }}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
                <button
                  onClick={() => {
                    setIsEditingTitle(false);
                    setTitleDraft('');
                  }}
                  style={{ padding: '8px 14px', borderRadius: 8, background: '#fff', border: '1px solid #E5E7EB', cursor: 'pointer' }}
                >
                  取消
                </button>
                <button
                  onClick={() => {
                    if (titleDraft.trim() && onUpdateTitle) {
                      onUpdateTitle(titleDraft.trim());
                    }
                    setIsEditingTitle(false);
                  }}
                  disabled={!titleDraft.trim()}
                  style={{
                    padding: '8px 14px',
                    borderRadius: 8,
                    background: titleDraft.trim() ? '#FF6B35' : '#F3F4F6',
                    color: titleDraft.trim() ? '#fff' : '#9CA3AF',
                    border: 'none',
                    cursor: titleDraft.trim() ? 'pointer' : 'not-allowed',
                  }}
                >
                  保存
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* 摘要編輯 */}
        {isEditingSummary ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 12, marginTop: 12, width: '100%', maxWidth: 600, margin: '12px auto 0' }}>
            <textarea
              value={summaryDraft}
              onChange={(e) => setSummaryDraft(e.target.value)}
              placeholder="輸入遊記摘要（選填）"
              autoFocus
              rows={3}
              style={{
                fontSize: 16,
                border: '2px solid #FF6B35',
                borderRadius: 8,
                padding: '12px 16px',
                width: '100%',
                outline: 'none',
                resize: 'vertical',
                lineHeight: 1.6,
                boxSizing: 'border-box',
              }}
            />

            {/* Tag Editor */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
               {/* Selected Tags */}
               {tagsDraft.length > 0 && (
                 <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {tagsDraft.map(tag => (
                      <span key={tag} style={{ 
                        background: '#EFF6FF', 
                        color: '#2563EB', 
                        padding: '4px 8px', 
                        borderRadius: 4, 
                        fontSize: 14,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4
                      }}>
                        #{tag} 
                        <button 
                          onClick={() => removeTag(tag)}
                          style={{ border: 'none', background: 'transparent', color: '#2563EB', cursor: 'pointer', padding: 0, fontSize: 14 }}
                        >
                          ✕
                        </button>
                      </span>
                    ))}
                 </div>
               )}
               
               {/* Toolbar */}
               <div style={{ display: 'flex', gap: 8, position: 'relative' }}>
                  <button 
                    onClick={() => setShowTagPicker(!showTagPicker)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 16,
                      border: '1px solid #E5E7EB',
                      background: '#F9FAFB',
                      color: '#374151',
                      fontSize: 13,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4
                    }}
                  >
                    # 話題
                  </button>
                  <button 
                    disabled 
                    style={{
                      padding: '6px 12px',
                      borderRadius: 16,
                      border: '1px solid #E5E7EB',
                      background: '#F9FAFB',
                      color: '#9CA3AF',
                      fontSize: 13,
                      cursor: 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4
                    }}
                  >
                    @ 用戶
                  </button>
                  <button 
                    disabled 
                    style={{
                      padding: '6px 12px',
                      borderRadius: 16,
                      border: '1px solid #E5E7EB',
                      background: '#F9FAFB',
                      color: '#9CA3AF',
                      fontSize: 13,
                      cursor: 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4
                    }}
                  >
                    投票
                  </button>

                  {/* Tag Picker Popup */}
                  {showTagPicker && (
                    <div style={{ 
                      position: 'absolute', 
                      top: '100%', 
                      left: 0, 
                      marginTop: 8, 
                      background: '#fff', 
                      border: '1px solid #E5E7EB', 
                      borderRadius: 8, 
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)', 
                      padding: 8, 
                      zIndex: 10,
                      width: 240,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8
                    }}>
                       <input 
                         placeholder="搜索或創建話題" 
                         value={tagSearch} 
                         onChange={(e) => setTagSearch(e.target.value)} 
                         autoFocus
                         style={{
                           padding: '6px 8px',
                           borderRadius: 4,
                           border: '1px solid #D1D5DB',
                           fontSize: 14,
                           width: '100%',
                           boxSizing: 'border-box'
                         }}
                       />
                       <div style={{ display: 'flex', flexDirection: 'column', maxHeight: 150, overflowY: 'auto' }}>
                          {filteredTags.map(tag => (
                            <button 
                              key={tag} 
                              onClick={() => addTag(tag)}
                              style={{
                                textAlign: 'left',
                                padding: '6px 8px',
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: 14,
                                color: '#374151',
                                borderRadius: 4,
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.background = '#F3F4F6'}
                              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                            >
                              #{tag}
                            </button>
                          ))}
                          {tagSearch && !filteredTags.includes(tagSearch) && (
                            <button 
                              onClick={() => addTag(tagSearch)}
                              style={{
                                textAlign: 'left',
                                padding: '6px 8px',
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: 14,
                                color: '#2563EB',
                                borderRadius: 4,
                                fontWeight: 500
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.background = '#EFF6FF'}
                              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                            >
                              創建 "#{tagSearch}"
                            </button>
                          )}
                       </div>
                    </div>
                  )}
               </div>
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
              <button
                onClick={() => {
                  setIsEditingSummary(false);
                  setSummaryDraft('');
                  setTagsDraft([]);
                }}
                style={{
                  padding: '6px 16px',
                  borderRadius: 6,
                  border: '1px solid #D1D5DB',
                  background: '#fff',
                  cursor: 'pointer',
                  fontSize: 14,
                }}
              >
                取消
              </button>
              <button
                onClick={() => {
                  if (onUpdateSummaryAndTags) {
                    onUpdateSummaryAndTags(summaryDraft.trim(), tagsDraft);
                  } else {
                    if (onUpdateSummary) {
                      onUpdateSummary(summaryDraft.trim());
                    }
                    if (onUpdateTags) {
                      onUpdateTags(tagsDraft);
                    }
                  }
                  setIsEditingSummary(false);
                }}
                style={{
                  padding: '6px 16px',
                  borderRadius: 6,
                  border: 'none',
                  background: '#FF6B35',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                保存
              </button>
            </div>
          </div>
        ) : (
          <>
            {resolvedSummary ? (
              <div style={{ textAlign: 'center' }}>
                <p 
                  style={{ 
                    fontSize: 18, 
                    color: '#4B5563', 
                    lineHeight: 1.6, 
                    margin: '0 auto',
                    cursor: onUpdateSummary ? 'pointer' : 'default',
                    display: 'inline-block',
                    maxWidth: 600,
                    textAlign: 'center',
                  }}
                  onClick={() => {
                    if (onUpdateSummary) {
                      setSummaryDraft(resolvedSummary);
                      setTagsDraft(draft?.tags || []);
                      setIsEditingSummary(true);
                    }
                  }}
                  onMouseEnter={(e) => {
                    if (onUpdateSummary) {
                      e.currentTarget.style.color = '#FF6B35';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = '#4B5563';
                  }}
                >
                  {resolvedSummary} {onUpdateSummary && <PlatformIcon name="edit" size={16} color="#9CA3AF" family="MaterialIcons" />}
                </p>
                {draft?.tags && draft.tags.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginTop: 8 }}>
                    {draft.tags.map(tag => (
                      <span key={tag} style={{ color: '#2563EB', fontSize: 14 }}>#{tag}</span>
                    ))}
                  </div>
                )}
              </div>
            ) : null}
          </>
        )}
        
        {/* lower summary add button removed — use the header-side button instead */}
      </section>

      {composedEntries.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#9CA3AF', padding: 48 }}>此天已沒有可發佈的卡片</div>
      ) : (
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 24 }}>
          {composedEntries.map(({ item, snapshotEntry, hidden, removeHandler, restoreHandler, showConnector, dayId, entryId }, idx) => {
            const composerOpen = activeComposer === entryId;
            const composerValue = noteDrafts[entryId] || '';
            const isSaving = savingEntryId === entryId;
            const canShowComposer = Boolean(!hidden && canComposeNotes && dayId && snapshotEntry);
            const cardId = snapshotEntry?.meta?.card_id;
            const isUploading = uploadingCardId === cardId;
            
            return (
              <div key={snapshotEntry?.id || item.id || idx} style={{ width: '100%' }}>
                {/* 隐藏的文件输入 */}
                {cardId && (
                  <input
                    ref={(el) => { if (cardId) fileInputRefs.current[cardId] = el; }}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp,image/avif"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file && cardId) {
                        handleImageUpload(cardId, file);
                      }
                    }}
                  />
                )}
                
                <TimelineItemRow time={String(idx + 1)} showConnectorBelow={showConnector}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <EntryCard
                      item={item}
                      snapshotEntry={snapshotEntry}
                      hidden={hidden}
                      onRemove={removeHandler}
                      onRestore={restoreHandler}
                      onUploadImage={cardId ? () => fileInputRefs.current[cardId]?.click() : undefined}
                      uploading={isUploading}
                      onImageClick={openLightbox}
                    />
                    <EntryNotesList notes={snapshotEntry?.notes} />
                    {canShowComposer ? (
                      composerOpen ? (
                        <div
                          style={{
                            background: '#fff',
                            borderRadius: 26,
                            border: '1px solid #E5E7EB',
                            padding: '16px 18px',
                            boxShadow: '0 16px 40px rgba(15,23,42,0.12)',
                            width: '100%',
                            boxSizing: 'border-box',
                          }}
                        >
                          <textarea
                            value={composerValue}
                            onChange={(e) => updateComposerValue(entryId, e.target.value)}
                            placeholder="記錄心情、待辦、小事項..."
                            style={{
                              width: '100%',
                              minHeight: 90,
                              border: '1px solid #E5E7EB',
                              borderRadius: 20,
                              padding: '12px 14px',
                              fontSize: 14,
                              resize: 'vertical',
                              color: '#111827',
                              boxSizing: 'border-box',
                            }}
                          />
                          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 12 }}>
                            <button
                              type="button"
                              onClick={() => closeComposer(entryId)}
                              style={{
                                padding: '8px 16px',
                                borderRadius: 999,
                                border: '1px solid #E5E7EB',
                                background: '#fff',
                                cursor: 'pointer',
                              }}
                            >
                              取消
                            </button>
                            <button
                              type="button"
                              disabled={!composerValue.trim() || isSaving}
                              onClick={() => submitComposer(entryId, dayId)}
                              style={{
                                padding: '8px 20px',
                                borderRadius: 999,
                                border: 'none',
                                background: (!composerValue.trim() || isSaving) ? '#FCD34D' : '#FB923C',
                                color: '#fff',
                                cursor: (!composerValue.trim() || isSaving) ? 'not-allowed' : 'pointer',
                                fontWeight: 600,
                              }}
                            >
                              {isSaving ? '儲存中...' : '加入筆記'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => openComposer(entryId)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '10px 18px',
                            borderRadius: 28,
                            background: '#fff',
                            border: '1px solid rgba(230,230,230,1)',
                            boxShadow: '0 6px 18px rgba(0,0,0,0.06)',
                            cursor: 'pointer',
                            color: '#9CA3AF',
                            fontSize: 14,
                          }}
                        >
                          <PlatformIcon name="edit_note" size={20} color="#9CA3AF" family="MaterialIcons" />
                          <span>記錄心情、待辦、小事項...</span>
                        </button>
                      )
                    ) : null}
                  </div>
                </TimelineItemRow>
              </div>
            );
          })}
        </div>
      )}
      </div>

      {/* 灯箱 */}
      {lightboxState && typeof window !== 'undefined' && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            zIndex: 999999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={closeLightbox}
        >
          <img
            src={resolveImageUrl(lightboxState.images[lightboxState.currentIndex]?.url || lightboxState.images[lightboxState.currentIndex])}
            alt=""
            style={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              objectFit: 'contain',
            }}
            onClick={(e) => e.stopPropagation()}
          />
          
          {/* 关闭按钮 */}
          <button
            onClick={closeLightbox}
            style={{
              position: 'absolute',
              top: 20,
              right: 20,
              width: 40,
              height: 40,
              borderRadius: '50%',
              border: 'none',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              color: '#000',
              fontSize: 20,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
            }}
          >
            <MdClose size={24} />
          </button>
          
          {lightboxState.images.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  prevImage();
                }}
                style={{
                  position: 'absolute',
                  left: 20,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  border: 'none',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  color: '#333',
                  fontSize: 28,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                }}
              >
                <MdChevronLeft size={32} />
              </button>
              
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  nextImage();
                }}
                style={{
                  position: 'absolute',
                  right: 20,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  border: 'none',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  color: '#333',
                  fontSize: 28,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                }}
              >
                <MdChevronRight size={32} />
              </button>

              {/* 图片计数 */}
              <div style={{
                position: 'absolute',
                top: 20,
                left: '50%',
                transform: 'translateX(-50%)',
                padding: '8px 16px',
                borderRadius: 20,
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                color: '#fff',
                fontSize: 14,
                fontWeight: 600,
              }}>
                {lightboxState.currentIndex + 1} / {lightboxState.images.length}
              </div>
            </>
          )}
        </div>
      )}
      {/* Floating publish FAB (bottom-right) */}
      {onPublish ? (
        <button
          type="button"
          onClick={onPublish}
          disabled={publishing}
          title="提交發佈"
          style={{
            position: 'fixed',
            right: 18,
            bottom: fabBottomInset,
            zIndex: 200000,
            width: 48,
            height: 48,
            borderRadius: 24,
            border: 'none',
            background: publishing ? '#FCD34D' : '#F97316',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 24px rgba(249,115,22,0.24)',
            cursor: publishing ? 'not-allowed' : 'pointer',
          }}
        >
          {publishing ? (
            <PlatformIcon name="hourglass_empty" size={20} color="#fff" family="MaterialIcons" />
          ) : (
            <PlatformIcon name="send" size={20} color="#fff" family="MaterialIcons" />
          )}
        </button>
      ) : null}
    </div>
  );
};

export default TravelPostPreviewLayout;